import { useEffect, useRef, useState } from 'react';
import {
  fetchPollResults, filteredSessionsFor, pollKey, shortMeta, submitFeedback, submitVote, suggestSessions,
  type PollResults, type Session, type Strategy,
} from '@ce/core';
import { trackColor } from '@ce/theme';
import { useTheme } from '../ThemeProvider';
import { btn, divider, kicker, saveStyle, t } from '../styles';
import { Sheen } from '../components/Ambient';
import { hashText, propsOf, type WidgetCtx, type WidgetProps } from './context';

const TENANT = (ctx: WidgetCtx) => ctx.bundle.tenantId;

function Title({ children }: { children: string }) {
  return children ? <div style={{ ...kicker(), margin: '24px 0 11px' }}>{children}</div> : null;
}

/**
 * A poll.
 *
 * Identity is the widget id plus a hash of the options, so editing the options
 * starts a fresh poll instead of silently re-labelling votes already cast —
 * "Runway" would otherwise inherit whatever the third option used to be.
 *
 * Voting is idempotent per device on the server and one-shot on the device, so
 * a retry on the next mount is safe. Percentages appear only when the server
 * has answered: showing invented ones offline would be worse than showing none.
 */
export function Poll({ ctx, inst }: WidgetProps) {
  const { state, dispatch, cmsUrl } = ctx;
  const p = propsOf<{ question: string; options: string[]; results: 'afterVote' | 'always' | 'never' }>(inst);
  const key = pollKey(inst.id, p.options);
  const voted = state.pollVotes[key];
  const [results, setResults] = useState<PollResults | null>(null);
  const retried = useRef(false);

  const wantsResults = p.results === 'always' || (p.results === 'afterVote' && voted !== undefined);

  useEffect(() => {
    if (!wantsResults || !cmsUrl) return;
    let alive = true;
    const pull = () => fetchPollResults(cmsUrl, TENANT(ctx), key).then((r) => alive && r && setResults(r));
    pull();
    const id = window.setInterval(pull, 8000);
    return () => { alive = false; window.clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsResults, cmsUrl, key]);

  // A vote cast offline is re-sent once when the widget next mounts.
  useEffect(() => {
    if (voted === undefined || retried.current || !cmsUrl) return;
    retried.current = true;
    void submitVote(cmsUrl, TENANT(ctx), key, state.deviceId, voted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voted, cmsUrl]);

  const vote = (i: number) => {
    if (voted !== undefined) return;
    dispatch({ type: 'votePoll', id: key, option: i });
    void submitVote(cmsUrl, TENANT(ctx), key, state.deviceId, i);
  };

  const total = results?.total ?? 0;

  return (
    <div style={{ marginTop: 24, padding: 16, borderRadius: 'var(--radius-md)', border: `1px solid ${divider}`, background: 'var(--color-surface)' }}>
      <div style={kicker(9.5, 'var(--color-accent-300)')}>Poll</div>
      <div style={{ font: '500 16px/1.3 var(--font-body)', margin: '9px 0 13px' }}>{p.question}</div>
      <div style={{ display: 'grid', gap: 8 }}>
        {p.options.map((label, i) => {
          const mine = voted === i;
          const count = results?.counts[i] ?? 0;
          const pct = total ? Math.round((count / total) * 100) : 0;
          const showBar = wantsResults && results !== null;
          return (
            <button
              key={label + i}
              type="button"
              disabled={voted !== undefined}
              onClick={() => vote(i)}
              aria-pressed={mine}
              style={{
                position: 'relative', overflow: 'hidden', textAlign: 'left', padding: '11px 13px',
                borderRadius: 'var(--radius-sm)', color: 'var(--color-text)',
                cursor: voted === undefined ? 'pointer' : 'default',
                border: `1px solid ${mine ? 'var(--color-accent)' : divider}`,
                background: 'transparent', font: '500 13.5px/1.3 var(--font-body)',
              }}
            >
              {showBar && (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`,
                    background: mine ? 'rgba(var(--color-accent-rgb), 0.28)' : 'rgba(var(--color-text-rgb), 0.08)',
                    transition: 'width .6s cubic-bezier(.2,.8,.2,1)',
                  }}
                />
              )}
              <span style={{ position: 'relative', display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ flex: 1 }}>{label}</span>
                {mine && <span style={{ color: 'var(--color-accent-300)', fontSize: 12 }}>{'\u2713'}</span>}
                {showBar && <span style={{ font: '500 12px/1 var(--font-mono)', color: t(0.6) }}>{pct}%</span>}
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: t(0.42), marginTop: 11 }}>
        {voted === undefined
          ? 'One vote per device.'
          : results
            ? `${total} vote${total === 1 ? '' : 's'} so far`
            : p.results === 'never'
              ? 'Thanks \u2014 your vote is in.'
              : 'Vote saved on this device. Results appear when you\u2019re back online.'}
      </div>
    </div>
  );
}

const FACES = ['\u{1F61E}', '\u{1F615}', '\u{1F610}', '\u{1F642}', '\u{1F604}'];

/** A one-tap rating with an optional comment. */
export function LiveFeedback({ ctx, inst }: WidgetProps) {
  const { state, dispatch, cmsUrl } = ctx;
  const p = propsOf<{
    prompt: string; style: 'stars' | 'emoji' | 'thumbs'; target: 'event' | 'session';
    sessionId: string; allowComment: boolean; thanks: string;
  }>(inst);
  const targetId = p.target === 'session' && p.sessionId ? p.sessionId : 'event';
  const key = `${inst.id}:${targetId}`;
  const given = state.feedbackGiven[key];
  const scale = p.style === 'thumbs' ? 2 : 5;
  const [pick, setPick] = useState<number | null>(null);
  const [comment, setComment] = useState('');

  const send = (rating: number, text?: string) => {
    dispatch({ type: 'giveFeedback', key, rating });
    void submitFeedback(cmsUrl, TENANT(ctx), {
      widgetId: inst.id, targetKind: p.target, targetId, rating, scale, comment: text?.trim() || undefined, deviceId: state.deviceId,
    });
  };

  const choose = (rating: number) => {
    // With a comment box, the rating waits for the note; without, one tap is the whole interaction.
    if (p.allowComment) setPick(rating);
    else send(rating);
  };

  if (given !== undefined) {
    return (
      <div style={{ marginTop: 24, padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--color-accent-700)', background: 'var(--color-accent-900)' }}>
        <div style={kicker(9.5, 'var(--color-accent-300)')}>Feedback</div>
        <div style={{ fontSize: 13, lineHeight: 1.55, color: t(0.8), marginTop: 9 }}>{p.thanks}</div>
      </div>
    );
  }

  const shown = pick ?? 0;

  return (
    <div style={{ position: 'relative', overflow: 'hidden', marginTop: 24, padding: 16, borderRadius: 'var(--radius-md)', border: `1px solid ${divider}`, background: 'var(--color-surface)' }}>
      <Sheen delay={6} strength={0.05} />
      <div style={{ ...kicker(9.5, 'var(--color-accent-300)'), position: 'relative' }}>Feedback</div>
      <div style={{ font: '500 16px/1.3 var(--font-body)', margin: '9px 0 14px', position: 'relative' }}>{p.prompt}</div>

      <div style={{ display: 'flex', gap: p.style === 'thumbs' ? 12 : 6, justifyContent: p.style === 'thumbs' ? 'flex-start' : 'space-between', position: 'relative' }}>
        {Array.from({ length: scale }, (_, i) => i + 1).map((n) => {
          const on = p.style === 'stars' ? n <= shown : n === pick;
          return (
            <button
              key={n}
              type="button"
              aria-label={p.style === 'thumbs' ? (n === 2 ? 'Thumbs up' : 'Thumbs down') : `${n} out of ${scale}`}
              aria-pressed={n === pick}
              onClick={() => choose(n)}
              style={{
                flex: p.style === 'thumbs' ? 'none' : 1, minWidth: 48, height: 48, cursor: 'pointer',
                borderRadius: 'var(--radius-sm)', display: 'grid', placeItems: 'center',
                border: `1px solid ${on ? 'var(--color-accent)' : divider}`,
                background: on ? 'rgba(var(--color-accent-rgb), 0.18)' : 'transparent',
                color: on ? 'var(--color-accent-300)' : t(0.5),
                fontSize: p.style === 'stars' ? 20 : 22,
                transform: on ? 'scale(1.05)' : 'none',
                transition: 'transform .18s cubic-bezier(.2,.8,.2,1), background .18s ease, border-color .18s ease',
              }}
            >
              {p.style === 'stars' ? '\u2605' : p.style === 'emoji' ? FACES[n - 1] : n === 2 ? '\u{1F44D}' : '\u{1F44E}'}
            </button>
          );
        })}
      </div>

      {p.allowComment && pick !== null && (
        <div style={{ marginTop: 12, position: 'relative' }}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={280}
            rows={2}
            placeholder="Anything you\u2019d like to add? (optional)"
            aria-label="Comment"
            style={{
              width: '100%', resize: 'none', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
              border: `1px solid ${divider}`, background: 'var(--color-bg)', color: 'var(--color-text)',
              font: '400 13px/1.45 var(--font-body)', outline: 'none',
            }}
          />
          <button type="button" onClick={() => send(pick, comment)} style={btn('primary', { width: '100%', height: 40, marginTop: 8 })}>
            Send feedback
          </button>
        </div>
      )}
    </div>
  );
}

interface QuizQ { q: string; a: string[]; correct: number }

/** A short quiz that pays once per question. */
export function QuizCard({ ctx, inst }: WidgetProps) {
  const { bundle, state, dispatch } = ctx;
  const p = propsOf<{
    title: string; source: 'content' | 'custom'; start: number; count: number; points: number;
    questions: QuizQ[];
  }>(inst);

  const questions: QuizQ[] = p.source === 'custom'
    ? p.questions.map((q) => ({ ...q, correct: q.correct - 1 }))   // 1-based in the editor
    : bundle.quests.quiz.slice(p.start, p.start + p.count).map((q) => ({ q: q.q, a: q.a, correct: q.correct }));

  const [idx, setIdx] = useState(0);
  const [pick, setPick] = useState<number | null>(null);
  if (!questions.length) return null;

  const q = questions[Math.min(idx, questions.length - 1)];
  // Hashing the question text means editing it re-opens it for points.
  const key = `${inst.id}:${hashText(q.q)}`;
  const already = state.quizAnswered.includes(key);
  const revealed = pick !== null || already;
  const last = idx >= questions.length - 1;

  const choose = (i: number) => {
    if (revealed) return;
    setPick(i);
    if (i === q.correct) dispatch({ type: 'answerQuiz', key, points: p.points });
    else dispatch({ type: 'answerQuiz', key, points: 0 });
  };

  return (
    <div style={{ marginTop: 24, padding: 16, borderRadius: 'var(--radius-md)', border: `1px solid ${divider}`, background: 'var(--color-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, ...kicker(9.5, 'var(--color-accent-300)') }}>{p.title}</div>
        {questions.length > 1 && (
          <div style={{ font: '400 10px/1 var(--font-mono)', color: t(0.45) }}>{idx + 1}/{questions.length}</div>
        )}
      </div>
      <div style={{ font: '500 15.5px/1.35 var(--font-body)', margin: '10px 0 13px' }}>{q.q}</div>
      <div style={{ display: 'grid', gap: 8 }}>
        {q.a.map((label, i) => {
          const right = i === q.correct;
          const chosen = pick === i;
          const show = revealed && (right || chosen);
          return (
            <button
              key={label + i}
              type="button"
              disabled={revealed}
              onClick={() => choose(i)}
              style={{
                textAlign: 'left', padding: '11px 13px', borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text)', cursor: revealed ? 'default' : 'pointer',
                font: '500 13.5px/1.3 var(--font-body)',
                border: `1px solid ${show && right ? 'var(--color-accent)' : chosen ? t(0.4) : divider}`,
                background: show && right ? 'rgba(var(--color-accent-rgb), 0.2)' : 'transparent',
                opacity: revealed && !show ? 0.5 : 1,
                transition: 'all .2s ease',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
      {revealed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <div style={{ flex: 1, fontSize: 12.5, color: t(0.7) }}>
            {already && pick === null
              ? 'You\u2019ve answered this one.'
              : pick === q.correct
                ? `+${p.points} points \u2014 correct.`
                : `Not quite. It\u2019s ${q.a[q.correct]}.`}
          </div>
          {!last && (
            <button type="button" onClick={() => { setIdx(idx + 1); setPick(null); }} style={btn('primary', { fontSize: 12.5 })}>
              Next
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SessionRow({
  ctx, session, reasons, showStar,
}: {
  ctx: WidgetCtx; session: Session; reasons?: string[]; showStar: boolean;
}) {
  const theme = useTheme();
  const { state, dispatch } = ctx;
  const on = state.saved.includes(session.id);
  return (
    <div
      style={{
        display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface)', boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div style={{ width: 2, alignSelf: 'stretch', borderRadius: 2, background: trackColor(theme, session.track), flex: 'none' }} />
      <button
        type="button"
        onClick={() => dispatch({ type: 'openSession', id: session.id })}
        style={{ flex: 1, minWidth: 0, textAlign: 'left', border: 0, padding: 0, background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}
      >
        <div style={{ font: '500 14.5px/1.25 var(--font-body)' }}>{session.title}</div>
        <div style={{ fontSize: 11.5, color: t(0.52), marginTop: 4 }}>{shortMeta(session)}</div>
        {reasons && reasons.length > 0 && (
          <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--color-accent-300)', marginTop: 6 }}>
            {reasons.join(' \u00b7 ')}
          </div>
        )}
      </button>
      {showStar && (
        <button
          type="button"
          aria-label={on ? `Unstar ${session.title}` : `Star ${session.title}`}
          onClick={() => dispatch({ type: 'toggleSave', id: session.id })}
          style={saveStyle(on)}
        >
          {on ? '\u2605' : '\u2606'}
        </button>
      )}
    </div>
  );
}

/** Suggestions from behaviour. On-device, so instant and free per attendee. */
export function SmartSuggestions({ ctx, inst }: WidgetProps) {
  const p = propsOf<{ title: string; strategy: Strategy; limit: number; showReasons: boolean }>(inst);
  const picks = suggestSessions(ctx.bundle, ctx.state, { strategy: p.strategy, limit: p.limit });
  if (!picks.length) return null;
  return (
    <div>
      <Title>{p.title}</Title>
      <div style={{ display: 'grid', gap: 8 }}>
        {picks.map((s) => (
          <SessionRow key={s.session.id} ctx={ctx} session={s.session} reasons={p.showReasons ? s.reasons : undefined} showStar />
        ))}
      </div>
    </div>
  );
}

export function SessionList({ ctx, inst }: WidgetProps) {
  const p = propsOf<{ title: string; track: string; day: string; limit: number; showStar: boolean }>(inst);
  const day = p.day === 'selected' ? ctx.state.day : p.day;
  const list = filteredSessionsFor(ctx.bundle, day, p.track).slice(0, p.limit);
  if (!list.length) return null;
  return (
    <div>
      <Title>{p.title}</Title>
      <div style={{ display: 'grid', gap: 8 }}>
        {list.map((s) => <SessionRow key={s.id} ctx={ctx} session={s} showStar={p.showStar} />)}
      </div>
    </div>
  );
}
